import type { ReactNode } from "react";

/**
 * الأيقونات — مجموعة واحدة، بخطٍّ واحد وحجم واحد.
 *
 * تُطلَب بالاسم فقط، فلا تُلصَق `svg` في شاشة. ومقاسها يأتي من CSS لا من
 * الخاصية، فتكبر وتصغر مع الكثافة بلا تعديل في مئة موضع.
 */

const PATHS: Record<string, ReactNode> = {
  /* مفتاح — طلبات الصلاحيات */
  key: (
    <>
      <circle cx="7.5" cy="15.5" r="4.5" />
      <path d="M10.7 12.3 21 2m-3 3 2.5 2.5M15 8l2.5 2.5" />
    </>
  ),
  /* درع — الأدوار والصلاحيات */
  shield: <path d="M12 2.5 20 6v6c0 5-3.4 8.4-8 9.5-4.6-1.1-8-4.5-8-9.5V6z" />,
  /* مبنى — الأقسام */
  building: (
    <>
      <path d="M4 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16M16 9h2a2 2 0 0 1 2 2v10" />
      <path d="M8 7h4M8 11h4M8 15h4M2 21h20" />
    </>
  ),
  home: (
    <>
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <path d="M9 22V12h6v10" />
    </>
  ),
  check: (
    <>
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </>
  ),
  bell: (
    <>
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </>
  ),
  pulse: <path d="M22 12h-4l-3 9L9 3l-3 9H2" />,
  list: (
    <>
      <path d="M8 6h13M8 12h13M8 18h13" />
      <path d="M3 6h.01M3 12h.01M3 18h.01" />
    </>
  ),
  sliders: (
    <>
      <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3" />
      <path d="M1 14h6M9 8h6M17 16h6" />
    </>
  ),
  book: (
    <>
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </>
  ),
  user: (
    <>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </>
  ),
  more: (
    <>
      <circle cx="12" cy="12" r="1" />
      <circle cx="19" cy="12" r="1" />
      <circle cx="5" cy="12" r="1" />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </>
  ),
  moon: <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8" />,
  close: <path d="M18 6 6 18M6 6l12 12" />,
  eye: (
    <>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  /* عينٌ مشطوبة — «مخفي عن العميل» على بطاقة عنصر الخطة */
  eyeOff: (
    <path d="M3 3l18 18M10.6 5.1A10 10 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3.2 4M6.6 6.6C3.8 8.4 2 12 2 12s3.5 7 10 7c1.6 0 3-.4 4.3-1" />
  ),
  /* خروج — في القوقعة، فيظهر للموظف وللعميل معاً */
  logout: (
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5M21 12H9" />
    </>
  ),
  /* تقويم — الخطط وجدول النشر */
  calendar: (
    <>
      <rect x="3" y="4.5" width="18" height="16" rx="2" />
      <path d="M3 9.5h18M8 2.5v4M16 2.5v4" />
    </>
  ),
  /* ناس — فريق العميل وجهات اتصاله */
  users: (
    <>
      <path d="M16 20v-1.5a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4V20" />
      <circle cx="9" cy="7" r="3.5" />
      <path d="M22 20v-1.5a4 4 0 0 0-3-3.9M16.5 3.6a4 4 0 0 1 0 7.3" />
    </>
  ),
  /* قاعدة بيانات — ناحية التخزين */
  database: (
    <>
      <ellipse cx="12" cy="5.5" rx="8" ry="3" />
      <path d="M4 5.5v13c0 1.7 3.6 3 8 3s8-1.3 8-3v-13M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3" />
    </>
  ),
  /* رسالة — المحادثة */
  message: (
    <path d="M21 11.5a8 8 0 0 1-8.5 8 9 9 0 0 1-3.9-.9L3 20.5l1.9-5.1A8 8 0 0 1 4 11.5a8 8 0 0 1 8.5-8 8 8 0 0 1 8.5 8z" />
  ),
  /* رسم بياني — التقارير */
  chart: (
    <>
      <path d="M3 3v18h18" />
      <path d="M7 15l3.5-4 3 3L20 7" />
    </>
  ),
  /* مشبك — عددُ مرفقات الصفّ (موك أب المهام المباشرة) */
  clip: (
    <path d="M21 12.5l-8.5 8.5a5 5 0 0 1-7-7L14 5.5a3.3 3.3 0 0 1 4.7 4.7l-8.5 8.5a1.7 1.7 0 0 1-2.4-2.4L15.5 8.6" />
  ),
  "eye-off": (
    <>
      <path d="M10.6 5.2A9.6 9.6 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3 3.9M6.6 6.6A17 17 0 0 0 2 12s3.5 7 10 7a9.6 9.6 0 0 0 4.2-1" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2M2 2l20 20" />
    </>
  ),
  /* شاشة — جهازٌ مكتبيّ في «الأجهزة اللي حسابك مفتوح عليها» */
  monitor: <path d="M3 5h18v11H3zM8 20h8M12 16v4" />,
  /* تليفون — جهازٌ محمول في القائمة نفسها */
  phone: (
    <path d="M8 2h8a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1zM11 18h2" />
  ),
};

/** الأسماءُ الموجودة فعلاً — تقرؤها البوابة، فلا اسمَ يسقط إلى «…» بصمت */
export const ICON_NAMES: readonly string[] = Object.keys(PATHS);

/**
 * أيقونةٌ بالاسم.
 *
 * **والاسمُ الغائب يسقط إلى «…»**، وذلك ما أخفى ثلاثةَ أسماء: `calendar`
 * و`database` و`users` — فبندُ «الخطط» في قائمة الموظفين، و«ناحية التخزين»،
 * و«فريقنا» في البورتال **كلُّها كانت تُرسَم ثلاثَ نقاط**. ولا اختبارَ يسقط:
 * السقوطُ الاحتياطي يُنتج شيئاً صالحاً دائماً.
 *
 * وظهر بالنقر، **وأُغلق الصنفُ ببوابة**: كلُّ اسمٍ يذكره بندُ تنقّلٍ أو شاشةٌ
 * بورتالية **موجودٌ هنا**، وإلا كُسر البناء.
 */
export function Icon({ name }: { readonly name: string }): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name] ?? PATHS["more"]}
    </svg>
  );
}
